import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Nav from "@/components/site/Nav";
import Sidebar from "@/components/site/Sidebar";
import Footer from "@/components/site/Footer";
import Assistant from "@/components/site/Assistant";
import AliasEntry from "@/components/site/AliasEntry";
import Home from "@/pages/Home";
import Compass from "@/components/site/Compass";
import PlatformPage from "@/pages/PlatformPage";
import ClinicDesk from "@/pages/ClinicDesk";
import MentalHealth from "@/pages/MentalHealth";
import Vitality from "@/pages/Vitality";
import Sleep from "@/pages/Sleep";
import Reproductive from "@/pages/Reproductive";
import Relationships from "@/pages/Relationships";
import Prevention from "@/pages/Prevention";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Why from "@/pages/Why";
import Contact from "@/pages/Contact";
import Record from "@/pages/Record";
import { PLATFORMS, byPath } from "@/lib/platforms";

const CUSTOM_PLATFORM_KEYS = ["health", "mental", "vitality", "sleep", "reproductive", "relationships", "prevention"];
const GENERIC_PLATFORMS = PLATFORMS.filter((p) => !CUSTOM_PLATFORM_KEYS.includes(p.key));


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function CursorRing() {
  const wrapRef = useRef(null);
  const [big, setBig] = useState(false);
  const [on, setOn] = useState(false);
  useEffect(() => {
    let tx = 0, ty = 0, x = 0, y = 0, raf;
    const move = (e) => {
      setOn(true);
      tx = e.clientX; ty = e.clientY;
      const t = e.target.closest("a, button, [role='button'], input, textarea, select");
      setBig(!!t);
    };
    const loop = () => {
      x += (tx - x) * 0.18;
      y += (ty - y) * 0.18;
      if (wrapRef.current) wrapRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", move, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => { window.removeEventListener("mousemove", move); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={wrapRef} className="cursor-wrap" aria-hidden="true">
      <div className={`cursor-ring ${on ? "on" : ""} ${big ? "big" : ""}`} />
      <div className={`cursor-core ${on ? "on" : ""}`} />
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const platform = byPath(location.pathname);
  const veilColor = platform ? platform.hue : "#2E3338";
  const first = useRef(true);
  useEffect(() => { first.current = false; }, []);

  return (
    <div className="relative">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/health" element={<ClinicDesk />} />
            <Route path="/mental" element={<MentalHealth />} />
            <Route path="/vitality" element={<Vitality />} />
            <Route path="/sleep" element={<Sleep />} />
            <Route path="/reproductive" element={<Reproductive />} />
            <Route path="/relationships" element={<Relationships />} />
            <Route path="/prevention" element={<Prevention />} />
            {GENERIC_PLATFORMS.map((p) => (
              <Route key={p.key} path={p.path} element={<PlatformPage platformKey={p.key} />} />
            ))}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/why" element={<Why />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/record" element={<Record />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
      {!first.current && (
        <motion.div
          key={`veil-${location.pathname}`}
          className="fixed inset-0 z-[70] pointer-events-none"
          style={{ background: veilColor, transformOrigin: "top" }}
          initial={{ scaleY: 1, opacity: 1 }}
          animate={{ scaleY: 0, opacity: 0.9 }}
          transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
        />
      )}
    </div>
  );
}

function GlobalCompass() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener("nc-open-compass", openIt);
    return () => window.removeEventListener("nc-open-compass", openIt);
  }, []);
  return <Compass open={open} onOpenChange={setOpen} />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <CursorRing />
          <Sidebar />
          <div className="md:pl-20">
            <Nav />
            <AnimatedRoutes />
            <Footer />
          </div>
          <Assistant />
          <AliasEntry />
          <GlobalCompass />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
